import styled from '@emotion/styled';
import React from 'react';
import { useSelector } from 'react-redux';
import Flag from 'react-world-flags';

import { getSortedCountries } from 'src/modules/countries/selectors/countrySelectors';
import {
    OptionComponent,
    Select,
    SelectField,
    SelectFieldProps,
    SelectProps,
    ValueComponent,
    MultiSelectField,
    MultiSelectFieldProps,
} from '@modernhealth/vital-components';
import { color, font } from 'src/shared/styles/variables';

const selectStyles = {
    control: () => ({
        width: '100%',
        marginBottom: 14,
    }),
};

const OptionText = styled.span(
    {
        color: color.fiord,
    },
    font.heavy(font.size.base),
);

const FlagText = styled(OptionText)({
    marginBottom: -3,
    marginLeft: 14,
});

export interface CountryOption {
    label: string;
    value: string;
    isoCode: string;
}

interface Props {
    testID?: string;
    value?: string | null;
    onChange?: (country: CountryOption | null) => void;
}

const CountrySelectOption: OptionComponent<CountryOption> = ({ option }) => {
    return (
        <div css={{ textAlign: 'left' }}>
            <Flag alt="" code={option.isoCode} width="36" height="24" />
            <FlagText>{option.label}</FlagText>
        </div>
    );
};

const CountrySelectValue: ValueComponent<CountryOption> = CountrySelectOption;

const CountrySelectFieldComponent = SelectField.withCustomOption(
    CountrySelectOption,
    CountrySelectValue,
);

const CountrySelectComponent = Select.withCustomOption(CountrySelectOption, CountrySelectValue);

interface CountrySelectProps extends SelectProps<CountryOption>, Props {}
interface CountrySelectFieldProps extends SelectFieldProps<CountryOption>, Props {}
interface CountryMultiSelectFieldProps extends MultiSelectFieldProps<CountryOption> {
    testID?: string;
}

export const CountrySelect: React.FC<CountrySelectProps & { id: string }> = ({
    testID,
    id,
    value,
    defaultValue,
    onChange,
}) => {
    const countries = useSelector(getSortedCountries);

    return (
        <CountrySelectComponent
            id={id}
            selectStyle={selectStyles.control}
            data-testid={testID}
            onChange={onChange}
            defaultValue={defaultValue}
            value={value}
            options={countries.map(
                ({ isoCode, text, value: optionValue }): CountryOption => ({
                    isoCode,
                    label: text,
                    value: optionValue,
                }),
            )}
        />
    );
};

export const CountrySelectField: React.FC<CountrySelectFieldProps> = ({
    testID,
    fieldId,
    label,
    error,
    value,
    defaultValue,
    onChange,
}) => {
    const countries = useSelector(getSortedCountries);

    return (
        <CountrySelectFieldComponent
            fieldId={fieldId}
            label={label}
            error={error}
            selectStyle={selectStyles.control}
            data-testid={testID}
            onChange={countryOption => {
                onChange?.(countryOption);
            }}
            defaultValue={defaultValue}
            value={value}
            options={countries.map(
                ({ isoCode, text, ...option }): CountryOption => ({
                    isoCode,
                    label: text,
                    value: option.value,
                }),
            )}
        />
    );
};

export const CountryMultiSelectField: React.FC<CountryMultiSelectFieldProps> = ({
    testID,
    fieldId,
    label,
    error,
    value,
    defaultValue,
    onChange,
}) => {
    const countries = useSelector(getSortedCountries);

    return (
        <MultiSelectField
            fieldId={fieldId}
            label={label}
            error={error}
            selectStyle={selectStyles.control}
            data-testid={testID}
            onChange={countryOption => {
                onChange?.(countryOption);
            }}
            defaultValue={defaultValue}
            value={value}
            options={countries.map(
                ({ isoCode, text, ...option }): CountryOption => ({
                    isoCode,
                    label: text,
                    value: option.value,
                }),
            )}
        />
    );
};
